import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useAI } from './AIContext';
import aiIcon from '../../assets/ai-icon.svg';

interface BulletPointProps {
  desc: { id: string; text: string };
  expIndex: number;
  isEditingLocked: boolean;
  isFirstBulletPoint?: boolean;
  onUpdateText: (descId: string, newText: string) => void;
  onTailorBulletPoint: (expIndex: number, descId: string) => Promise<void>;
  onAcceptSuggestion: (expIndex: number, descId: string) => void;
  placeholderText: string;
  showAIButton: boolean;
  aiButtonWidth: number;
  suggestionWidth: number;
  getEditableFieldClasses: (isEmpty: boolean, baseClass: string) => string;
  onClearSelection?: () => void;
}

const BulletPoint: React.FC<BulletPointProps> = ({
  desc,
  expIndex,
  isEditingLocked,
  isFirstBulletPoint = false,
  onUpdateText,
  onTailorBulletPoint,
  onAcceptSuggestion,
  placeholderText,
  showAIButton,
  aiButtonWidth,
  suggestionWidth,
  getEditableFieldClasses,
  onClearSelection
}) => {
  const { selectedBulletPoint, expandedBulletPoint, clearAIState } = useAI();
  const [isFadingOut, setIsFadingOut] = useState(false);
  const bulletPointRef = useRef<HTMLDivElement>(null);

  // Check if this bullet point is currently selected for AI processing
  const isAISelected = selectedBulletPoint?.expIndex === expIndex && selectedBulletPoint?.descId === desc.id;
  const isExpanded = expandedBulletPoint?.expIndex === expIndex && expandedBulletPoint?.descId === desc.id;

  const handleTextUpdate = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    const newText = e.currentTarget?.innerText ?? desc.text;
    onUpdateText(desc.id, newText);
  }, [desc.id, desc.text, onUpdateText]);

  const handleAITailor = useCallback(() => {
    onTailorBulletPoint(expIndex, desc.id);
    onClearSelection?.();
  }, [expIndex, desc.id, onTailorBulletPoint, onClearSelection]);

  const handleAccept = useCallback(() => {
    // First update the bullet point text
    onAcceptSuggestion(expIndex, desc.id);
    // Trigger fade-out animation; we will clear AI state when it finishes
    setIsFadingOut(true);
  }, [expIndex, desc.id, onAcceptSuggestion]);

  const handleDismiss = useCallback(() => {
    // Only animate fade-out, no change to bullet content
    setIsFadingOut(true);
  }, []);

  // Auto-focus the first bullet point when component mounts to show cursor
  useEffect(() => {
    if (isFirstBulletPoint && bulletPointRef.current) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        bulletPointRef.current?.focus();
        // Position cursor at the BEGINNING of existing text
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(bulletPointRef.current!);
        range.collapse(true); // true = collapse to beginning
        selection?.removeAllRanges();
        selection?.addRange(range);
      }, 200);
    }
  }, [isFirstBulletPoint]);

  return (
    <li
      className={
        `${isExpanded ? "highlighted" : ""} ${isFadingOut ? "fade-highlight" : ""}`
      }
      data-exp-index={expIndex}
      data-desc-id={desc.id}
    >
      <div
        ref={bulletPointRef}
        className={`${getEditableFieldClasses(!desc.text, "description")} ${isFirstBulletPoint ? 'first-bullet-point' : ''}`}
        contentEditable={true}
        suppressContentEditableWarning
        onBlur={handleTextUpdate}
        data-placeholder={placeholderText}
      >
        {desc.text}
      </div>

      {/* AI Tailor Button */}
      {showAIButton && (
        <div
          className={'tailor-ai-btn-container visible'}
          style={{
            position: 'absolute',
            width: aiButtonWidth ? `${aiButtonWidth}px` : 'auto',
            maxWidth: '100%',
            zIndex: 1000,
            left: '80%',
            transform: 'translateX(-50%)'
          }}
        >
          <button
            className="tailor-ai-btn"
            onClick={handleAITailor}
          >
            <img 
              src={aiIcon}
              alt="AI Icon" 
              width={20} 
              height={20}
              style={{ filter: 'invert(58%) sepia(7%) saturate(98%) hue-rotate(314deg) brightness(89%) contrast(86%)' }}
            />
            <span className="btn-text">Tailor with AI</span>
          </button>
        </div>
      )}

      {/* AI Suggestion Panel */}
      {selectedBulletPoint && isAISelected && (
        <div 
          className={`bullet-point-suggestion ${isFadingOut ? "fade-out" : ""}`} 
          role="button"
          onTransitionEnd={() => {
            if (isFadingOut) {
              clearAIState();
              setIsFadingOut(false);
            }
          }}
          style={{ 
            width: suggestionWidth ? `${suggestionWidth}px` : 'auto',
            maxWidth: '100%'
          }}
        >
          <div className="suggestion-header">
            <img 
              src={aiIcon}
              alt="AI Icon" 
              width={20} 
              height={20}
              style={{ filter: 'invert(58%) sepia(7%) saturate(98%) hue-rotate(314deg) brightness(89%) contrast(86%)' }}
            />
            <span className="suggestion-title">
              Suggestion
            </span>
          </div>
          
          {selectedBulletPoint.isLoading ? (
            <div className="skeleton-lines">
              <div className="skeleton-loader" style={{ width: '100%' }}></div>
              <div className="skeleton-loader" style={{ width: '60%' }}></div>
            </div>
          ) : (
            <>
              {selectedBulletPoint.suggested ? (
                <div>{selectedBulletPoint.suggested}</div>
              ) : (
                ""
              )}
              <button
                className="bullet-point-button"
                onClick={handleAccept}
              >
                Approve
              </button>
              <button 
                className="bullet-point-button secondary"
                onClick={handleDismiss}
              >
                Dismiss
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
};

// Memoize to prevent re-renders unless props actually change
export default memo(BulletPoint, (prevProps, nextProps) => {
  return (
    prevProps.desc.id === nextProps.desc.id &&
    prevProps.desc.text === nextProps.desc.text &&
    prevProps.isEditingLocked === nextProps.isEditingLocked &&
    prevProps.showAIButton === nextProps.showAIButton
  );
}); 